import type { BlendshapeFrame, Sentiment } from '../types';
import type { AvatarSessionContext } from '../session';

export const AVATAR_EMBED_COMMAND = {
  speak: 'speak',
  setMood: 'setMood',
  setListening: 'setListening',
  setThinking: 'setThinking',
  playBlendshapes: 'playBlendshapes',
  sessionContext: 'session.context',
  stopAllAnimations: 'stopAllAnimations',
  playIdleAnimation: 'playIdleAnimation',
  setAppearance: 'setAppearance',
  applyConfig: 'applyConfig'
} as const;

export const AVATAR_EMBED_EVENT = {
  ready: 'avatar.ready',
  speakingStarted: 'avatar.speaking.started',
  speakingEnded: 'avatar.speaking.ended',
  speakingLegacy: 'avatar.speaking',
  moodChanged: 'avatar.mood.changed',
  speechInput: 'avatar.speech.input',
  speechRecognized: 'avatar.speech.recognized',
  speakError: 'avatar.speak.error',
  configApplied: 'avatar.config.applied'
} as const;

export type AvatarEmbedCommandType =
  typeof AVATAR_EMBED_COMMAND[keyof typeof AVATAR_EMBED_COMMAND];

export type AvatarEmbedEventType =
  typeof AVATAR_EMBED_EVENT[keyof typeof AVATAR_EMBED_EVENT];

export interface AvatarEmbedEnvelope<TType extends string, TPayload = undefined> {
  type: TType;
  payload?: TPayload;
}

export interface AvatarSpeakCommandPayload {
  text: string;
  ssml?: string | null;
  voice?: string | null;
}

export interface AvatarMoodCommandPayload {
  mood: Sentiment | string;
  intensity?: number;
}

export interface AvatarListeningCommandPayload {
  listening: boolean;
}

export interface AvatarThinkingCommandPayload {
  thinking: boolean;
}

export interface AvatarBlendshapeCommandPayload {
  frames: BlendshapeFrame[];
}

export interface AvatarAppearanceCommandPayload {
  appearance: Record<string, string | undefined>;
}

export interface AvatarApplyConfigCommandPayload {
  config: Record<string, unknown>;
}

export interface AvatarSessionContextPayload extends Partial<AvatarSessionContext> {
  liveMode?: boolean;
  mode?: 'live';
}

export interface AvatarSpeechEventPayload {
  text?: string;
  transcript?: string;
}

export interface AvatarMoodEventPayload {
  mood: string;
}

export interface AvatarErrorEventPayload {
  error?: string;
  message?: string;
}

export type AvatarEmbedCommandEnvelope =
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.speak, AvatarSpeakCommandPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.setMood, AvatarMoodCommandPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.setListening, AvatarListeningCommandPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.setThinking, AvatarThinkingCommandPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.playBlendshapes, AvatarBlendshapeCommandPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.sessionContext, AvatarSessionContextPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.stopAllAnimations>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.playIdleAnimation>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.setAppearance, AvatarAppearanceCommandPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_COMMAND.applyConfig, AvatarApplyConfigCommandPayload>;

export type AvatarEmbedEventEnvelope =
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.ready>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.speakingStarted, AvatarSpeechEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.speakingEnded, AvatarSpeechEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.speakingLegacy, AvatarSpeechEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.moodChanged, AvatarMoodEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.speechInput, AvatarSpeechEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.speechRecognized, AvatarSpeechEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.speakError, AvatarErrorEventPayload>
  | AvatarEmbedEnvelope<typeof AVATAR_EMBED_EVENT.configApplied, Record<string, unknown>>;

export function isAvatarEmbedEnvelope(value: unknown): value is AvatarEmbedEnvelope<string, unknown> {
  return Boolean(
    value
    && typeof value === 'object'
    && 'type' in (value as Record<string, unknown>)
    && typeof (value as Record<string, unknown>).type === 'string'
  );
}
